import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./../App.css";

import timelineElements from "./timelineElements";
import timelineElementsESP from "./timelineElementsESP";
import ProjectModal from "./Modal";
import ProjectCard from "./ProjectCard";

function TimelineApp({ language }) {
    const sliderRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedProjectIndex, setSelectedProjectIndex] = useState(null);
    const [selectedProjectKey, setSelectedProjectKey] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Multi-select stat filtering (can highlight several stats at once)
    const [selectedStats, setSelectedStats] = useState([]);

    const navigate = useNavigate();

    const projectsData = (language === "es" ? timelineElementsESP : timelineElements)
        .filter(project => project.showInProjects !== false);

    const stats = useMemo(
        () => [
            {
                id: "ai_ml",
                label: language === "es" ? "IA/ML Aplicaciones" : "AI/ML Applications",
                keywords: [
                    "rasa",
                    "nlp",
                    "nlu",
                    "convolutional",
                    "1d-cnn",
                    "cnn",
                ],
            },
            {
                id: "fullstack",
                label: language === "es" ? "Desarrollo Full-Stack" : "Full-Stack Development",
                keywords: [
                    "nodejs",
                    "node",
                    "react",
                    "reactjs",
                    "asp.net",
                    "aspnet",
                    "c#",
                    "csharp",
                    "mvc",
                    "front-end",
                    "web development",
                    "desarrollo web",
                    "p2p",
                    "actressmas",
                    "backend",
                    "recruitment",
                    "rasa",
                    "python"
                ],
            },
            {
                id: "game",
                label: language === "es" ? "Desarrollo de Videojuegos" : "Game Development",
                keywords: [
                    "unity",
                    "game development",
                    "chimpaquiz",
                    "fizh&chips",
                    "cmake",
                    
                ],
            },
        ],
        [language]
    );

    const normalize = (s = "") =>
        String(s)
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s\-_.]/gu, " ")
            .replace(/\s+/g, " ")
            .trim();

    const escapeForRegex = (s = "") => s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

    // color map
    const statColorHex = {
        ai_ml: "#5865F2",
        data: "#10B981",
        fullstack: "#EC4899",
        game: "#F97316",
    };

    const hexToRgba = (hex = "#000000", alpha = 1) => {
        const h = hex.replace("#", "");
        const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // projects meta: matchedStats (prefer technologies tokens)
    const projectsWithMeta = useMemo(() => {
        const statSets = stats.map((s) => ({
            id: s.id,
            kws: s.keywords.map((k) => normalize(k)).filter(Boolean),
        }));

        return projectsData.map((p) => {
            const techTokens = (p.technologies || []).map((t) => normalize(t));
            const titleDesc = normalize(`${p.title || ""} ${p.description || ""}`);
            const combined = `${titleDesc} ${techTokens.join(" ")}`.trim();

            const matched = new Set();

            // 1) tech-token matches first
            for (const { id, kws } of statSets) {
                if (kws.length === 0) continue;
                const techMatch = techTokens.some((tk) => kws.some((kw) => tk === kw || tk.includes(kw)));
                if (techMatch) matched.add(id);
            }

            // 2) fallback to title/description if no tech matches
            if (matched.size === 0) {
                for (const { id, kws } of statSets) {
                    if (kws.length === 0) continue;
                    const any = kws.some((kw) => {
                        const rx = new RegExp(`\\b${escapeForRegex(kw)}\\b`, "i");
                        if (rx.test(combined)) return true;
                        return combined.includes(kw);
                    });
                    if (any) matched.add(id);
                }
            }

            return { ...p, matchedStats: Array.from(matched) };
        });
    }, [projectsData, stats]);

    // Always display all projects (visualize everything). Deduplicate by title.
    // When a stat is selected, put matching projects first (stable order).
    const displayedProjects = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const p of projectsWithMeta) {
            const key = normalize(p.title || `${p.date || ""}-${p.location || ""}`);
            if (!seen.has(key)) {
                seen.add(key);
                out.push(p);
            }
        }

        if (selectedStats && selectedStats.length > 0) {
            const matched = [];
            const others = [];
            for (const p of out) {
                const projectCats = p.matchedStats || [];
                const isMatch = selectedStats.some((s) => projectCats.includes(s));
                if (isMatch) matched.push(p);
                else others.push(p);
            }
            return [...matched, ...others];
        }

        return out;
    }, [projectsWithMeta, selectedStats]);

    // reset carousel on filter/language change
    useEffect(() => {
        setCurrentSlide(0);
        if (sliderRef.current && typeof sliderRef.current.slickGoTo === "function") {
            setTimeout(() => sliderRef.current.slickGoTo(0), 50);
        }
    }, [selectedStats, language]);

    const autoplayEnabled = !showModal && displayedProjects.length > 1;

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider || typeof slider.slickPause !== "function") return;

        let playTimeout = null;

        if (showModal) {
            slider.slickPause();
        } else if (autoplayEnabled && typeof slider.slickPlay === "function") {
            playTimeout = setTimeout(() => {
                const currentSlider = sliderRef.current;
                if (!currentSlider || typeof currentSlider.slickPlay !== "function") return;
                currentSlider.slickPlay();
            }, 150);
        }

        return () => {
            if (playTimeout) {
                clearTimeout(playTimeout);
            }
        };
    }, [showModal, autoplayEnabled]);

    const getProjectKey = useCallback((project, indexHint = 0) => {
        if (!project) return null;
        const compositeFallback = `${project.title || ""}|${project.date || ""}|${project.location || ""}|${project.link || ""}|${project.imgUrl || ""}`;
        return (
            project.key ||
            project.id ||
            project.slug ||
            project.title ||
            project.link ||
            project.imgUrl ||
            (compositeFallback.trim() ? compositeFallback : `project-${indexHint}`)
        );
    }, []);

    const selectProjectByIndex = useCallback(
        (index) => {
            if (!Array.isArray(displayedProjects) || displayedProjects.length === 0) {
                setSelectedProjectIndex(null);
                setSelectedProjectKey(null);
                return;
            }

            const total = displayedProjects.length;
            const normalizedIndex = ((index % total) + total) % total;
            const projectAtIndex = displayedProjects[normalizedIndex];
            setSelectedProjectIndex(normalizedIndex);
            setSelectedProjectKey(getProjectKey(projectAtIndex, normalizedIndex));
        },
        [displayedProjects, getProjectKey]
    );

    const handleShowModal = (project, indexHint = 0) => {
        const index = displayedProjects.findIndex((p) => p === project);
        const safeIndex = index >= 0 ? index : indexHint;
        selectProjectByIndex(safeIndex);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProjectIndex(null);
        setSelectedProjectKey(null);
    };

    const selectedProject = useMemo(() => {
        if (selectedProjectIndex === null) return null;
        if (
            typeof selectedProjectIndex !== "number" ||
            selectedProjectIndex < 0 ||
            selectedProjectIndex >= displayedProjects.length
        ) {
            return null;
        }
        return displayedProjects[selectedProjectIndex];
    }, [selectedProjectIndex, displayedProjects]);

    const showAdjacentProject = useCallback(
        (direction) => {
            if (displayedProjects.length === 0) return;

            const currentIndexValue =
                selectedProjectIndex !== null && typeof selectedProjectIndex === "number"
                    ? selectedProjectIndex
                    : direction === "next"
                    ? -1
                    : displayedProjects.length;

            const delta = direction === "next" ? 1 : -1;
            selectProjectByIndex(currentIndexValue + delta);
        },
        [displayedProjects, selectProjectByIndex, selectedProjectIndex]
    );

    const handleShowNextProject = useCallback(() => showAdjacentProject("next"), [showAdjacentProject]);
    const handleShowPreviousProject = useCallback(() => showAdjacentProject("prev"), [showAdjacentProject]);

    useEffect(() => {
        if (!showModal) return;
        if (displayedProjects.length === 0) {
            setShowModal(false);
            setSelectedProjectIndex(null);
            setSelectedProjectKey(null);
            return;
        }

        if (selectedProjectKey) {
            const locatedIndex = displayedProjects.findIndex((project, idx) => getProjectKey(project, idx) === selectedProjectKey);
            if (locatedIndex >= 0) {
                if (locatedIndex !== selectedProjectIndex) {
                    setSelectedProjectIndex(locatedIndex);
                }
                return;
            }
        }

        if (selectedProjectIndex !== null) {
            selectProjectByIndex(selectedProjectIndex);
        } else {
            selectProjectByIndex(0);
        }
    }, [displayedProjects, showModal, selectedProjectKey, selectedProjectIndex, getProjectKey, selectProjectByIndex]);

    // multi-toggle stat
    const toggleStat = (id) => {
        setSelectedStats((prev) => {
            const found = prev.includes(id);
            if (found) return prev.filter((x) => x !== id);
            return [...prev, id];
        });
    };

    const defaultSlidesToShow = 4;
    const slidesToShow = Math.max(1, Math.min(defaultSlidesToShow, displayedProjects.length || 1));

    const settings = {
        infinite: displayedProjects.length > slidesToShow,
        speed: 600,
        slidesToShow,
        slidesToScroll: 1,
        autoplay: autoplayEnabled,
        autoplaySpeed: 5000,
        cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        arrows: true,
        dots: displayedProjects.length > 1,
        pauseOnHover: true,
        beforeChange: (current, next) => setCurrentSlide(next),
        responsive: [
            { breakpoint: 1200, settings: { slidesToShow: Math.min(3, Math.max(1, displayedProjects.length || 1)), slidesToScroll: 1 } },
            { breakpoint: 900, settings: { slidesToShow: Math.min(2, Math.max(1, displayedProjects.length || 1)), slidesToScroll: 1 } },
            { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 1, arrows: false } },
        ],
    };

    return (
        <section className="skills-section" id="projects">
            <div className="cyber-background">
                <div className="cyber-grid"></div>
                <div className="floating-particles">
                    {[...Array(40)].map((_, i) => (
                        <div key={i} className={`particle particle-${i % 4 + 1}`}></div>
                    ))}
                </div>
            </div>

            <div className="skills-container">
                {/* Header Section */}
                <div className="skills-header">
                    <div className="header-content">
                        <div className="title-section">
                            <h2 className="main-title">
                                <span className="title-accent">{language === 'es' ? 'PROYECTOS' : 'PROJECTS'}</span>
                                <div className="title-underline" />
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Projects Carousel */}
                <div className="carousel-section">
                    <div className={`carousel-wrapper ${showModal ? "modal-active" : ""}`}>
                            {displayedProjects.length === 0 ? (
                                <div className="no-projects">No projects to show.</div>
                            ) : (
                                <Slider
                                    {...settings}
                                    ref={sliderRef}
                                    className={`skills-carousel ${showModal ? "paused" : ""}`}
                                >
                                    {displayedProjects.map((project, index) => {
                                        const key = project.key || project.title || `${project.date}-${index}`;

                                        // Determine which color to show:
                                        // - if no stat selected: show project's primary category color (first matchedStats) so colors are always visible
                                        // - if one or more stats selected: highlight with the first selected stat the project matches
                                        const projectCats = project.matchedStats || [];
                                        let colorHex = null;
                                        if (selectedStats.length === 0) {
                                            if (projectCats.length > 0) colorHex = statColorHex[projectCats[0]];
                                        } else {
                                            const firstSelectedMatch = selectedStats.find((s) => projectCats.includes(s));
                                            if (firstSelectedMatch) colorHex = statColorHex[firstSelectedMatch];
                                        }

                                        // If stats are selected and this project does NOT match any selected, dim it
                                        const dimWhenFiltering = selectedStats.length > 0 && !(projectCats.some((c) => selectedStats.includes(c)));

                                        const slideStyle = colorHex
                                            ? {
                                                  borderTop: `4px solid ${colorHex}`,
                                                  boxShadow: `0 8px 24px ${hexToRgba(colorHex, 0.10)}`,
                                                  backgroundClip: "padding-box",
                                              }
                                            : {};

                                        const wrapperStyle = {
                                            padding: 8,
                                            opacity: dimWhenFiltering ? 0.45 : 1,
                                            transition: "opacity 220ms ease",
                                        };

                                        return (
                                            <div key={key} className="skill-slide" style={wrapperStyle}>
                                                <div style={{ borderRadius: 12, overflow: "hidden", ...slideStyle }}>
                                                    <ProjectCard
                                                        imgUrl={project.imgUrl}
                                                        title={project.title}
                                                        onClick={() => handleShowModal(project, index)}
                                                        className={index === currentSlide ? "active" : ""}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Slider>
                            )}
                        </div>
                    </div>

                {/* Filter Section */}
                <div className="stats-section">
                    {stats.map((s) => (
                        <div
                            key={s.id}
                            className={`stat-item ${selectedStats.includes(s.id) ? "active-stat" : ""}`}
                            onClick={() => toggleStat(s.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") toggleStat(s.id);
                            }}
                            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}
                        >
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    background: selectedStats.includes(s.id) ? statColorHex[s.id] : "transparent",
                                    border: selectedStats.includes(s.id) ? "none" : "2px solid rgba(255,255,255,0.08)",
                                }}
                            />
                            <div className="stat-content">
                                <span className="stat-number">{s.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showModal && selectedProject && (
                <ProjectModal
                    onClose={handleCloseModal}
                    title={selectedProject.title}
                    description={selectedProject.description}
                    tech_definitions={selectedProject.tech_definitions}
                    imgUrl={selectedProject.imgUrl}
                    link={selectedProject.link}
                    videoUrl={selectedProject.videoUrl}
                    additionalLinks={selectedProject.additionalLinks}
                    languages_frameworks={selectedProject.languages_frameworks}
                    navigate={navigate}
                    language={language}
                    onShowNext={handleShowNextProject}
                    onShowPrevious={handleShowPreviousProject}
                    currentIndex={selectedProjectIndex ?? 0}
                    totalProjects={displayedProjects.length}
                />
            )}
        </section>
    );
}

export default TimelineApp;